import { useState, ChangeEvent, FormEvent } from 'react';
import Editor from '../../editor/Editor';
import { useNavigate } from 'react-router-dom';
import { gql, useMutation } from '@apollo/client';
import { Form, Button } from 'react-bootstrap';
import Loading from '../../common/Loading';
import CREATE_PRODUCT_MUTATION from './queries/CREATE_PRODUCT_MUTATION';
import AlertDismissible from '../../common/AlertDismissible';
import ImageSelector from './components/ImageSelector';
import { DUImage } from '../pages/types/DUImage';
import DateRange from '../../common/DateRange';

const CreateProduct = () => {
    const navigate = useNavigate();
    const [description, setDescription] = useState('');
    const [validated, setValidated] = useState(false);
    const [code, setCode] = useState('');
    const [cost, setCost] = useState<number>();
    const [image, setImage] = useState<DUImage>();
    const [stepQty, setStepQty] = useState<number>();
    const [maxQty, setMaxQty] = useState<number>();
    const [startDate, setStartDate] = useState<Date>();
    const [endDate, setEndDate] = useState<Date>();
    const [name, setName] = useState('');
    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState(false);
    const handleImageSelected = (selectedImage: DUImage) => {
        setImage(selectedImage);
    };
    const getEditedData = () => {
        return {
            name,
            description,
            code,
            cost,
            stepQty,
            maxQty,
            startDate,
            endDate,
            image,
        };
    }
    const [createProduct, { loading }] = useMutation(CREATE_PRODUCT_MUTATION, {
        variables: {
            productData: {
                ...getEditedData()
            },
        },
        update(cache, { data: { addProduct } }) {
            cache.modify({
                fields: {
                    products(existingProductRefs = []) {
                        const newProductRef = cache.writeFragment({
                            data: addProduct,
                            fragment: gql`
                        fragment NewProduct on Product {
                          id
                        }
                      `
                        });
                        return [...existingProductRefs, newProductRef];
                    }
                }
            });
        },
        onCompleted: (res) => {
            setSaving(false);
            setSuccess(true);
            navigate(`/admin/edit-product/${res.addProduct.id}`);
        }
    });
    
    const handleSubmit = (e: FormEvent) => {
        const form = e.currentTarget as HTMLFormElement;
        const isValid = form.checkValidity();
        e.preventDefault();
        e.stopPropagation();

        setValidated(true);
        if (isValid) {
            createProduct();
            setSaving(true);
        }
    };
    const renderProduct = () => {

        return (<Form noValidate validated={validated} onSubmit={handleSubmit}>
            {loading && <div className="mt-4 mb-4"><Loading /></div>}
            {success && <AlertDismissible variant="success" dismissible onClose={() => setSuccess(false)} className="mt-4">Product Saved.</AlertDismissible>}

            {<Form.Group className="mt-4" id="productCodeControl">
                <Form.Label>Code</Form.Label>
                <Form.Control onChange={(event: ChangeEvent<any>) => {
                    const changed: string = event.target.value || '';
                    setCode(changed);
                }} placeholder="Product Code" type="text" required />
            </Form.Group>}

            {<Form.Group className="mt-4" id="productNameControl">
                <Form.Label>Name</Form.Label>
                <Form.Control onChange={(event: ChangeEvent<any>) => {
                    const changed: string = event.target.value || '';
                    setName(changed);
                }} placeholder="Product Name" type="text" required />
            </Form.Group>}
            {<Form.Group className="mt-4" id="productCostControl">
                <Form.Label>Cost Per Item</Form.Label>
                <Form.Control onChange={(event: ChangeEvent<any>) => {
                    const changed: number = parseFloat(event.target.value) || 0;
                    setCost(changed);
                }} placeholder="Product Cost" type="number" step="0.01" required />

                <Form.Control.Feedback type="invalid">
                    Please provide a product cost.
                </Form.Control.Feedback>
            </Form.Group>}
            {<Form.Group className="mt-4" id="productStepQtyControl">
                <Form.Label>Multiples (Step Quantity)</Form.Label>
                <Form.Control onChange={(event: ChangeEvent<any>) => {
                    const changed: number = parseInt(event.target.value, 10) || 0;
                    setStepQty(changed);
                }} placeholder="Product Quantity Step" type="number" />
            </Form.Group>}
            {<Form.Group className="mt-4" id="productMaxQtyControl">
                <Form.Label>Max Quantity</Form.Label>
                <Form.Control onChange={(event: ChangeEvent<any>) => {
                    const changed: number = parseInt(event.target.value, 10) || 0;
                    setMaxQty(changed);
                }} placeholder="Product Max Quantity" type="number" />
            </Form.Group>}
            <Form.Group>
                <Form.Label>Valid Dates</Form.Label>                   
                <DateRange startDate={startDate} endDate={endDate} onStartDateChange={setStartDate} onEndDateChange={setEndDate} />
            </Form.Group>
            <ImageSelector handleSelected={handleImageSelected} selected={image} />
            {<Form.Group className="mt-4" id="productDescriptionControl">
                <Form.Label>Description</Form.Label>
                <Editor onChange={(changedDescription: string) => setDescription(changedDescription)} value="" />
            </Form.Group>}


            <Button type="submit" disabled={saving || loading} variant="primary" className="mt-4">Create Product</Button>
        </Form>);
    };
    return <div>{renderProduct()}</div>;
};

export default CreateProduct;
